import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Build as BuildIcon,
  CalendarMonth as CalendarIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  Logout as LogoutIcon,
  DirectionsCar as CarIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  ChevronLeft as ChevronLeftIcon,
  MenuBook as ManualIcon,
  History as HistoryIcon,
  CloudDownload as CloudDownloadIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { logout, setActiveRole, updateUser } from '../store/authSlice';
import { authService } from '../services/api';
import { ROLE_LABELS } from '../pages/Login';
import logo from '../assets/logo.png';

const drawerWidth = 260;
const collapsedDrawerWidth = 72;

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useSelector((state) => state.auth);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  // All'avvio riallinea l'utente al backend: ruoli e permessi (es. "può creare interventi")
  // possono essere cambiati dall'admin mentre l'utente era loggato, e la copia in
  // localStorage resterebbe vecchia. Se offline o token scaduto si tiene quella in cache.
  useEffect(() => {
    let annullato = false;
    authService
      .getCurrentUser()
      .then((res) => {
        if (!annullato) dispatch(updateUser(res.data));
      })
      .catch(() => {});
    return () => {
      annullato = true;
    };
  }, [dispatch]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleSwitchRole = async (role) => {
    handleMenuClose();
    try {
      const res = await authService.switchRole(role);
      dispatch(setActiveRole({ role: res.data.active_role, token: res.data.access_token }));
      navigate(['admin', 'super_admin', 'amministratore'].includes(res.data.active_role) ? '/dashboard' : '/calendario');
    } catch (e) {
      // ruolo non disponibile: ignora
    }
  };

  const otherRoles = (user?.roles || []).filter((r) => r !== user?.role);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', roles: ['admin', 'super_admin', 'amministratore'] },
    { text: 'Clienti', icon: <BusinessIcon />, path: '/clienti', roles: ['admin', 'super_admin', 'amministratore'] },
    { text: 'Cantieri', icon: <BuildIcon />, path: '/cantieri', roles: ['admin', 'super_admin', 'amministratore'] },
    { text: 'Interventi', icon: <AssignmentIcon />, path: '/interventi', roles: ['admin', 'super_admin', 'amministratore', 'capocantiere', 'tecnico'] },
    { text: 'Calendario', icon: <CalendarIcon />, path: '/calendario', roles: ['admin', 'super_admin', 'amministratore', 'capocantiere', 'tecnico'] },
    { text: 'Veicoli', icon: <CarIcon />, path: '/veicoli', roles: ['admin', 'super_admin', 'amministratore'] },
    { text: 'Rapportini', icon: <DescriptionIcon />, path: '/rapportini', roles: ['admin', 'super_admin', 'amministratore', 'capocantiere'] },
    { text: 'Registro Attività', icon: <HistoryIcon />, path: '/registro-attivita', roles: ['admin', 'super_admin', 'amministratore'] },
    { text: 'Utenti', icon: <PeopleIcon />, path: '/utenti', roles: ['admin', 'super_admin'] },
    { text: 'Manuale', icon: <ManualIcon />, path: '/manuale', roles: ['admin', 'super_admin', 'amministratore', 'capocantiere', 'tecnico'] },
  ];

  const filteredMenuItems = menuItems.filter((item) =>
    item.roles.includes(user?.role)
  );

  // Funzione per ottenere le iniziali dell'utente
  const getInitials = (nome, cognome) => {
    return `${nome?.charAt(0) || ''}${cognome?.charAt(0) || ''}`.toUpperCase();
  };

  const actualDrawerWidth = collapsed && !isMobile ? collapsedDrawerWidth : drawerWidth;

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0a0a0a',
        color: 'white',
      }}
    >
      {/* Header Sidebar con Logo */}
      <Box
        sx={{
          p: collapsed && !isMobile ? 1.5 : 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed && !isMobile ? 'center' : 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          minHeight: 72,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="NC Elettrica"
              sx={{
                width: 32,
                height: 32,
                objectFit: 'contain',
              }}
            />
          </Box>
          <Collapse in={!collapsed || isMobile} orientation="horizontal">
            <Box sx={{ whiteSpace: 'nowrap' }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  fontSize: '1rem',
                  letterSpacing: '-0.02em',
                  color: 'white',
                  lineHeight: 1.2,
                }}
              >
                NC Elettrica
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '0.65rem',
                }}
              >
                Gestione Cantieri
              </Typography>
            </Box>
          </Collapse>
        </Box>

        {/* Collapse button - solo desktop */}
        {!isMobile && (
          <Collapse in={!collapsed} orientation="horizontal">
            <IconButton
              onClick={() => setCollapsed(!collapsed)}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Collapse>
        )}
      </Box>

      {/* Menu Items */}
      <Box sx={{ flex: 1, py: 2, overflowY: 'auto', overflowX: 'hidden' }}>
        <List sx={{ px: collapsed && !isMobile ? 1 : 1.5 }}>
          {filteredMenuItems.map((item) => {
            const isActive = location.pathname === item.path ||
                           (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <Tooltip
                  title={collapsed && !isMobile ? item.text : ''}
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    selected={isActive}
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                    sx={{
                      py: 1.25,
                      px: collapsed && !isMobile ? 1.5 : 2,
                      borderRadius: 2,
                      justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                      transition: 'all 0.2s ease',
                      '&.Mui-selected': {
                        bgcolor: '#e50000',
                        '&:hover': {
                          bgcolor: '#c50000',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'white',
                        },
                        '& .MuiListItemText-primary': {
                          color: 'white',
                          fontWeight: 600,
                        },
                      },
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.08)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive ? 'white' : 'rgba(255,255,255,0.6)',
                        minWidth: collapsed && !isMobile ? 0 : 40,
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <Collapse in={!collapsed || isMobile} orientation="horizontal">
                      <ListItemText
                        primary={item.text}
                        sx={{
                          whiteSpace: 'nowrap',
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                            fontWeight: isActive ? 600 : 500,
                            color: isActive ? 'white' : 'rgba(255,255,255,0.85)',
                          },
                        }}
                      />
                    </Collapse>
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Footer Sidebar - User Info */}
      <Box
        sx={{
          p: collapsed && !isMobile ? 1.5 : 2,
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Box
          onClick={handleMenuOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: collapsed && !isMobile ? 0.5 : 1.5,
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.08)',
            },
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: '#e50000',
              fontSize: '0.875rem',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {getInitials(user?.nome, user?.cognome)}
          </Avatar>
          <Collapse in={!collapsed || isMobile} orientation="horizontal">
            <Box sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'white',
                  fontSize: '0.8rem',
                  lineHeight: 1.3,
                }}
                noWrap
              >
                {user?.nome} {user?.cognome}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '0.7rem',
                  textTransform: 'capitalize',
                }}
              >
                {ROLE_LABELS[user?.role] || user?.role}
              </Typography>
            </Box>
            <ArrowDownIcon
              sx={{
                fontSize: 18,
                color: 'rgba(255,255,255,0.5)',
                flexShrink: 0,
              }}
            />
          </Collapse>
        </Box>

        {/* User Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
          PaperProps={{
            elevation: 0,
            sx: {
              mb: 1,
              minWidth: 220,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              '& .MuiMenuItem-root': {
                py: 1.25,
                px: 2,
                borderRadius: 1,
                mx: 1,
                my: 0.25,
                fontSize: '0.875rem',
                '&:hover': {
                  bgcolor: '#f3f4f6',
                },
              },
            },
          }}
        >
          {/* User Header in Menu */}
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: '#e50000',
                  fontSize: '1rem',
                  fontWeight: 700,
                }}
              >
                {getInitials(user?.nome, user?.cognome)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  {user?.nome} {user?.cognome}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ py: 1 }}>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/profilo'); }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Il mio profilo
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/impostazioni'); }}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Impostazioni
            </MenuItem>
            {user?.role === 'capocantiere' && (
              <MenuItem onClick={() => { handleMenuClose(); navigate('/impostazioni'); }}>
                <ListItemIcon>
                  <CloudDownloadIcon fontSize="small" />
                </ListItemIcon>
                Scarica dati giornata
              </MenuItem>
            )}
          </Box>

          {otherRoles.length > 0 && (
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', py: 1 }}>
              <Typography variant="caption" sx={{ px: 2.5, color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Cambia ruolo
              </Typography>
              {otherRoles.map((r) => (
                <MenuItem key={r} onClick={() => handleSwitchRole(r)}>
                  <ListItemIcon>
                    <SwapHorizIcon fontSize="small" />
                  </ListItemIcon>
                  Entra come {ROLE_LABELS[r] || r}
                </MenuItem>
              ))}
            </Box>
          )}

          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', py: 1 }}>
            <MenuItem
              onClick={handleLogout}
              sx={{
                color: '#dc2626',
                '&:hover': {
                  bgcolor: '#fef2f2 !important',
                },
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" sx={{ color: '#dc2626' }} />
              </ListItemIcon>
              Esci
            </MenuItem>
          </Box>
        </Menu>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <CssBaseline />

      {/* Mobile Header Bar - Solo per aprire il menu */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            bgcolor: '#0a0a0a',
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            gap: 1.5,
          }}
        >
          <IconButton
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            sx={{ color: 'white' }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component="img"
            src={logo}
            alt="NC Elettrica"
            sx={{ width: 28, height: 28, objectFit: 'contain' }}
          />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: 'white', fontSize: '0.95rem' }}
          >
            NC Elettrica
          </Typography>
        </Box>
      )}

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { md: actualDrawerWidth },
          flexShrink: { md: 0 },
          transition: 'all 0.3s ease',
        }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: actualDrawerWidth,
              border: 'none',
              transition: 'all 0.3s ease',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Senza minWidth:0 un flex item non scende sotto la larghezza "min-content"
          // del suo contenuto: su mobile (dove non c'è la width calcolata qui sotto)
          // una pagina larga faceva crescere il <main> oltre il viewport e comparivano
          // 17px di scroll orizzontale su Clienti e 256px su Dashboard.
          minWidth: 0,
          width: { md: `calc(100% - ${actualDrawerWidth}px)` },
          minHeight: '100vh',
          transition: 'all 0.3s ease',
          mt: { xs: '60px', md: 0 }, // Margine top solo per mobile (altezza header mobile)
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
